/**
 * Creates and manages the widget graphics layer lifecycle.
 */

import { useEffect, useRef, useState } from "react";
import APIHelper from "@giscon/utils.api-helper-esri";
import LayerStore from "@giscon/utils.layer-store";

export default function useGraphicsLayer({ map, view, keepOnTop = true }) {
  const layerRef = useRef(null);
  const [graphicsLayer, setGraphicsLayer] = useState(null);

  useEffect(() => {
    if (!map || !view || layerRef.current) return;

    const createdLayer = LayerStore.addLayer(APIHelper.createGraphicsLayer());
    layerRef.current = createdLayer;
    setGraphicsLayer(createdLayer);
    createdLayer.addToMap(map, view);

    const onChanged = (e) => {
      if (!keepOnTop) return;
      if (e?.added && layerRef.current) {
        LayerStore.moveLayer(layerRef.current, 100);
      }
    };

    const maybeRemoveHandler = LayerStore.addChangedHandler?.(onChanged);

    return () => {
      try {
        if (typeof maybeRemoveHandler === "function") {
          maybeRemoveHandler();
        } else if (LayerStore.removeChangedHandler) {
          LayerStore.removeChangedHandler(onChanged);
        }
      } catch (e) {
        // ignore
      }

      try {
        layerRef.current?.removeFromMap?.(map, view);
      } catch (e) {
        // ignore
      }

      const index = LayerStore.layers.indexOf(layerRef.current);
      if (index > -1) {
        LayerStore.layers.splice(index, 1);
      }
      layerRef.current = null;
      setGraphicsLayer(null);
    };
  }, [map, view, keepOnTop]);

  return graphicsLayer;
}
